import {
  Avatar,
  FeedCard as SharedFeedCard,
  Icon,
  Tag,
  ModalRoot,
} from '@socialchorus/shared-ui-components';
import {
  ComponentProps,
  MouseEventHandler,
  ReactElement,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import useFeatureFlag from '../../../common/use-feature-flag';
import useInview from '../../../common/use-inview';
import { usePatronSelector } from '../../../common/use-patron-selector';
import { timeAgoOrDate } from '../../../lib/date-formatter';
import { contentSelectors } from '../../../models/content';
import {
  trackContentCardClick,
  trackContentCardView,
} from '../../../models/content/analytics';
import {
  getContentAuthorAvatarSrc,
  useContentAuthorLink,
} from '../../../models/content/helpers';
import { Content } from '../../../models/content/types';
import { Feature, getFeatureFlag } from '../../../models/features/features';
import { FeedCardActions } from './feed-card-actions';
import { useLinkNavigation } from '../../../common/use-link-navigation';
import { programSelectors } from '../../../models/program';
import ContentActionTranslateWrapper from '../../v2/content-actions/content-action-translate-wrapper';
import ContentFrame from '../content-frame';
import { useHistory } from 'react-router';
import styles from './solar-feed-card.module.scss';
import useScreenSize from '../../../common/use-screen-size';
import { ViewLikesModal } from '../../v2/content-actions/view-likes-modal/view-likes-modal';

function imageLabelWithIcon(iconName: string, text?: string): ReactElement {
  return (
    <SharedFeedCard.ImageLabel position="bottomRight">
      <Icon className="text-caption align-middle" size={16}>
        {iconName}
      </Icon>
      {text ? (
        <span className="text-caption ml-1 align-middle">{text}</span>
      ) : null}
    </SharedFeedCard.ImageLabel>
  );
}

export type FeedCardProps = {
  content: Content;
  analyticsData: IAnalyticsData;
  layout: 'horizontal' | 'vertical';
  compact?: boolean;
  titleHeadingLevel?: 2 | 3 | 4;
  fullHeight?: boolean;
  inlineVideo?: boolean;
  lineClamp?: 1 | 2 | 3 | 4 | 5 | 6;
  onClick?: () => void;
  showPinnedLabel?: boolean;
};

export function FeedCardWithData({
  content,
  analyticsData,
  layout: layoutProp,
  compact,
  titleHeadingLevel,
  fullHeight,
  onClick,
  lineClamp,
  inlineVideo = false,
  showPinnedLabel = false,
}: FeedCardProps) {
  const history = useHistory();
  const { width } = useScreenSize();

  //feed cards have a custom breakpoint, carried over from the old feed card.
  const isMobile = width < 600;
  const layout = isMobile ? 'vertical' : layoutProp;

  //controls minor layout changes associated with the reaction types deployment.
  const expandedReactionTypesDeployed = useFeatureFlag(
    Feature.EXPANDED_REACTION_TYPES_DEPLOY
  );
  //controls the reaction types feature
  const expandedReactionTypesEnabled = usePatronSelector((state) =>
    programSelectors.getExpandedReactionTypesEnabled(state)
  );

  const baseContentUrl = usePatronSelector((state) =>
    contentSelectors.getContentDetailUrl(state, { contentId: content.id })
  );
  const ugcEnabled = usePatronSelector((state) =>
    getFeatureFlag(state, Feature.UGC_ENABLED)
  );
  const isArchivedUgc = usePatronSelector(
    (state) =>
      ugcEnabled &&
      contentSelectors.getContentIsArchivedUgcV2(state, {
        contentId: content.id,
      })
  );
  const needsPreview = usePatronSelector(
    (state) =>
      ugcEnabled &&
      contentSelectors.getContentIsNonPublishedUgcV2(state, {
        contentId: content.id,
      })
  );
  const contentUrl = isArchivedUgc
    ? undefined
    : needsPreview
    ? `${baseContentUrl}/preview`
    : baseContentUrl;
  const reactions = usePatronSelector((state) =>
    contentSelectors.getContentReactions(state, { contentId: content.id })
  );
  const likeCount = usePatronSelector((s) =>
    contentSelectors.getContentLikeCount(s, { contentId: content.id })
  );
  const viewCount = usePatronSelector((s) =>
    contentSelectors.getContentViewCount(s, { contentId: content.id })
  );
  const viewsEnabled = usePatronSelector((s) =>
    programSelectors.getFeedCardViewsEnabled(s)
  );
  const publishedAt = usePatronSelector((s) =>
    contentSelectors.getContentPublishedAt(s, { contentId: content.id })
  );

  const isVideoType = content.content_type === 'video';

  //gotcha: must use the title from the selectors and not the passed in content object. the selectors will be updated with the translated title, while the content object is from the feed query on load.
  //to refactor: extract the content prop from here and only pass in the id, then use the selectors to get the content attributes
  const title = usePatronSelector((state) =>
    contentSelectors.getContentTitle(state, { contentId: content.id })
  );
  const description = usePatronSelector((state) =>
    contentSelectors.getContentSummary(state, { contentId: content.id })
  );

  const getLinkNavigation = useLinkNavigation();
  const { t } = useTranslation();

  const ref = useRef<HTMLElement>(null);

  //controls the video player being overlaid on the thumbnail.
  const [showVideo, setShowVideo] = useState<boolean>(false);
  const [showReactions, setShowReactions] = useState<boolean>(false);

  // Reset video state on location change
  useEffect(() => {
    let historyUnlisten;

    if (inlineVideo && showVideo) {
      historyUnlisten = history?.listen(() => {
        setShowVideo(false);
      });
    }
    return historyUnlisten;
  }, [showVideo, history, inlineVideo]);

  useInview({
    ref,
    once: true,
    onInview: () => trackContentCardView(content.id, analyticsData),
  });

  const contentImageUrl =
    content.feed_preview_image_urls[0] || content.background_image_url;

  let cardImage: ComponentProps<typeof SharedFeedCard.Image>['image'];
  let imageLabel: ReactElement | undefined;
  if (content.content_type === 'note') {
    cardImage = { type: 'note-icon', color: content.border_color };
  } else {
    cardImage = {
      url: contentImageUrl,
      alt: content.source_image_alt,
    };
    // Note: draft video content may have no video uploaded
    if (content.content_type === 'video' && content.video) {
      imageLabel = imageLabelWithIcon(
        'play_circle',
        content.video?.duration != null
          ? String(content.video.duration)
          : undefined
      );
    } else if (
      !!content.background_image_urls &&
      content.background_image_urls?.length > 1
    ) {
      imageLabel = imageLabelWithIcon(
        'image',
        content.background_image_urls.length + ''
      );
    }
  }

  const viewWord = t('content.view', {
    count: viewCount,
  });

  const avatarSrc = getContentAuthorAvatarSrc(content);

  const authorHref = useContentAuthorLink(content);
  const contentAuthorLink = authorHref
    ? getLinkNavigation(authorHref)
    : undefined;

  const additionalMetadata = [];
  if (expandedReactionTypesDeployed) {
    if (!expandedReactionTypesEnabled) {
      additionalMetadata.push(
        t('content.likes_with_count', { count: likeCount })
      );
    } else {
      const reactionTotal = reactions.reduce((a, b) => a + b.count, 0);
      additionalMetadata.push(
        t('content.reactions_with_count', {
          count: reactionTotal,
        })
      );
    }
  } else {
    additionalMetadata.push(publishedAt ? timeAgoOrDate(publishedAt) : '-');
  }
  if (viewsEnabled) {
    const viewDisplayText =
      t('compact_number', { value: viewCount }) + ` ${viewWord}`; //this is invalid for translation, the correct order of word and number is different per language. revisit when views is officially made into a feature.

    additionalMetadata.push(viewDisplayText);
  }

  const pinned = showPinnedLabel && content.pinned;

  const buttonText = content.buttons?.[0]?.buttonText; //featured label text
  const labelText = pinned
    ? t('content.pinned')
    : typeof buttonText === 'string' //guard against buttonText being something crazy like an object, this did happen in prod and caused a p1
    ? buttonText
    : undefined;

  const reactionsDisabled = useFeatureFlag(Feature.REACTIONS_DISABLED);

  const top3ReactionTypes =
    reactions
      .filter((r) => r.count > 0)
      .sort((a, b) => b.count - a.count)
      .slice(0, 3)
      .map((reaction) => reaction.emoji) || [];

  const handlePlayButtonClick: MouseEventHandler = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setShowVideo(true);
  };

  // Only allow clicking to open the reactions modal when there is at least
  // one like (legacy) or one reaction (expanded), otherwise keep it static.
  const reactionTotal = reactions.reduce((a, b) => a + b.count, 0);
  const metadataIsClickable =
    expandedReactionTypesDeployed &&
    (expandedReactionTypesEnabled ? reactionTotal > 0 : likeCount > 0);

  const metadataAriaLabel = expandedReactionTypesEnabled
    ? t('view_reactions_action')
    : t('view_likes_action');

  return (
    <>
      <SharedFeedCard.Root
        ref={ref}
        layout={layout}
        href={contentUrl ? getLinkNavigation(contentUrl) : undefined}
        fullHeight={fullHeight}
        compact={compact}
        title={title}
        description={description}
        headingLevel={titleHeadingLevel}
        generateMentionHref={(userId) =>
          getLinkNavigation(`/profiles/${userId}`)
        }
        onClick={() => {
          trackContentCardClick(content.id, analyticsData);
          onClick?.();
        }}
      >
        <SharedFeedCard.Image
          image={cardImage}
          renderOverride={(imageComponent) => {
            return inlineVideo && isVideoType ? (
              // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
              <div
                className={styles.VideoPlayContainer}
                onClick={(e) => {
                  //make sure clicking in this region doesn't trigger the card click
                  e.stopPropagation();
                  e.preventDefault();
                }}
              >
                {imageComponent}
                {!showVideo ? (
                  <button
                    className={styles.VideoPlayOverlayButton}
                    onClick={handlePlayButtonClick}
                  >
                    <Icon size={40} className={styles.VideoPlayIcon}>
                      play_circle
                    </Icon>
                  </button>
                ) : (
                  <ContentFrame
                    className={styles.VideoFrame}
                    contentId={content.id}
                    analyticsData={analyticsData}
                    autoPlayVideo={true} //autoplay on as this will only be rendered by a user clicking on the overlay
                  />
                )}
              </div>
            ) : (
              imageComponent
            );
          }}
        >
          {labelText ? (
            <SharedFeedCard.ImageLabel position="topRight">
              {content.pinned ? (
                <Icon className="text-caption align-middle" size={18}>
                  push_pin
                </Icon>
              ) : null}
              {labelText}
            </SharedFeedCard.ImageLabel>
          ) : undefined}
          {imageLabel}
        </SharedFeedCard.Image>
        <SharedFeedCard.Main>
          <SharedFeedCard.Content>
            {expandedReactionTypesDeployed && (
              <SharedFeedCard.ContentHeader
                authorAvatarComponent={
                  <Avatar imgSrc={avatarSrc} shape="circle" size="small" />
                }
                authorName={content.source_name}
                authorHref={contentAuthorLink}
                publishDate={publishedAt ? timeAgoOrDate(publishedAt) : '-'}
              />
            )}
            <SharedFeedCard.Title />
            <SharedFeedCard.Description lineClamp={lineClamp} />
            <ContentActionTranslateWrapper
              contentId={content.id}
              analyticsData={analyticsData}
            >
              {({ canTranslate, handleClick, isTranslated }) => {
                return canTranslate ? (
                  <SharedFeedCard.TranslateButton
                    isTranslated={isTranslated}
                    onClick={handleClick}
                  />
                ) : null;
              }}
            </ContentActionTranslateWrapper>
          </SharedFeedCard.Content>
          {
            <SharedFeedCard.Tags
              display={1}
              popoverTitle={t('content.posted_in_topics')}
              popoverWidth="200px"
              tags={content.content_channels.map((channel) => (
                <Tag
                  key={channel.id}
                  label={channel.name}
                  href={getLinkNavigation(`/channels/${channel.id}`)}
                />
              ))}
            />
          }
        </SharedFeedCard.Main>

        <SharedFeedCard.Footer>
          {metadataIsClickable ? (
            //assumption that metadataIsClickable already does the expandedReactionTypesDeployed check
            <SharedFeedCard.Metadata
              additionalMetadata={additionalMetadata}
              displayLikeIcon={!expandedReactionTypesEnabled}
              reactedEmojis={
                expandedReactionTypesEnabled ? top3ReactionTypes : undefined
              }
              onClick={() => setShowReactions(true)}
              ariaLabel={metadataAriaLabel}
            />
          ) : (
            <SharedFeedCard.Metadata
              additionalMetadata={additionalMetadata}
              displayLikeIcon={
                !!expandedReactionTypesDeployed && !expandedReactionTypesEnabled
              }
              reactedEmojis={
                expandedReactionTypesEnabled ? top3ReactionTypes : undefined
              }
              authorAvatarComponent={
                expandedReactionTypesDeployed ? undefined : (
                  <Avatar imgSrc={avatarSrc} shape="circle" size="small" />
                )
              }
              authorName={
                expandedReactionTypesDeployed ? undefined : content.source_name
              }
              authorHref={
                expandedReactionTypesDeployed ? undefined : contentAuthorLink
              }
            />
          )}

          {!reactionsDisabled ? (
            <FeedCardActions content={content} analyticsData={analyticsData} />
          ) : null}
        </SharedFeedCard.Footer>
      </SharedFeedCard.Root>

      <ModalRoot
        open={showReactions}
        onOpenChange={(setOpen) => setShowReactions(setOpen)}
      >
        <ViewLikesModal contentId={content.id} />
      </ModalRoot>
    </>
  );
}
